<template>
	<div
		v-if="queue.visible.value"
		class="upload-queue-panel"
		@mouseenter="queue.pauseAutoClose()"
		@mouseleave="queue.resumeAutoClose()"
	>
		<div class="upload-queue-panel__header">
			<span class="upload-queue-panel__title">
				{{ headerText }}
			</span>
			<app-icon
				v-if="queue.isActive.value"
				:class="{
					'upload-queue-panel__toggle': true,
					'upload-queue-panel__toggle--collapsed': collapsed
				}"
				name="chevron-down"
				width="18px"
				color="#979797"
				cursor="pointer"
				@click="collapsed = !collapsed"
			/>
			<app-icon
				v-else
				name="close-bold"
				width="18px"
				color="#979797"
				cursor="pointer"
				@click="queue.close()"
			/>
		</div>

		<div
			:class="{
				'upload-queue-panel__collapsible': true,
				'upload-queue-panel__collapsible--collapsed': collapsed
			}"
		>
			<div class="upload-queue-panel__collapsible-inner">
				<div class="upload-queue-panel__list">
					<div
						v-for="item in queue.items.value"
						:key="item.id"
						class="upload-queue-item"
						:class="`upload-queue-item--${item.status}`"
					>
						<app-icon
							class="upload-queue-item__icon"
							:name="getFileIcon(item.fileName)"
							width="18px"
							:color="iconColor(item)"
						/>

						<div class="upload-queue-item__body">
							<div class="upload-queue-item__row">
								<span
									class="upload-queue-item__name"
									:title="item.fileName"
								>
									{{ item.fileName }}
								</span>
								<span class="upload-queue-item__meta">
									{{ metaText(item) }}
								</span>
							</div>

							<div
								v-if="item.status === 'pending' || item.status === 'uploading'"
								class="upload-queue-item__progress"
							>
								<div
									class="upload-queue-item__progress-bar"
									:style="{ width: item.progress + '%' }"
								/>
							</div>
						</div>

						<app-tooltip
							v-if="item.status === 'pending' || item.status === 'uploading'"
							:text="$t('ui_cancel_upload')"
						>
							<template #activator>
								<app-icon
									name="close-bold"
									width="14px"
									color="#979797"
									cursor="pointer"
									@click="queue.cancel(item.id)"
								/>
							</template>
						</app-tooltip>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import AppIcon from '@/components/ui-kit/icon/AppIcon.vue'
import AppTooltip from '@/components/ui-kit/tooltip/AppTooltip.vue'
import { getFileIcon } from '@/utils/fileIcons'
import { useUploadQueue, type UploadItem } from '@/composables/useUploadQueue'
import { i18n } from '@/i18n'

const queue = useUploadQueue()

const collapsed = ref(false)

watch(
	() => queue.isActive.value,
	(active) => {
		if (!active) {
			collapsed.value = false
		}
	}
)

const headerText = computed(() => {
	if (queue.isActive.value) {
		return String(i18n.global.t('ui_uploading_files', {
			done: queue.doneCount.value,
			total: queue.totalCount.value,
		}))
	}
	if (queue.errorCount.value > 0) {
		return String(i18n.global.t('ui_upload_done_with_errors', { count: queue.errorCount.value }))
	}
	return String(i18n.global.t('ui_upload_complete'))
})

const iconColor = (item: UploadItem): string => {
	if (item.status === 'success') return '#438451'
	if (item.status === 'error') return '#e5484d'
	return '#979797'
}

const formatSize = (bytes: number): string => {
	if (bytes >= 1024 * 1024) {
		return String(i18n.global.t('ui_size_mb', { value: (bytes / (1024 * 1024)).toFixed(1) }))
	}
	if (bytes >= 1024) {
		return String(i18n.global.t('ui_size_kb', { value: Math.round(bytes / 1024) }))
	}
	return String(i18n.global.t('ui_size_bytes', { value: bytes }))
}

const metaText = (item: UploadItem): string => {
	switch (item.status) {
		case 'uploading':
			return `${item.progress}%`
		case 'success':
			return String(i18n.global.t('ui_done'))
		case 'error':
			return item.error || String(i18n.global.t('upload_error'))
		case 'canceled':
			return String(i18n.global.t('ui_canceled'))
		default:
			return formatSize(item.sizeBytes)
	}
}
</script>

<style scoped lang="scss">
.upload-queue-panel {
	position: fixed;
	right: 20px;
	bottom: 20px;
	z-index: 9999;
	width: 340px;
	max-width: calc(100vw - 40px);
	background: #ffffff;
	border: 1px solid #e6e6e6;
	border-radius: 10px;
	box-shadow: 0 8px 28px rgba(0, 0, 0, 0.16);
	overflow: hidden;
	font-family: inherit;

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 10px 12px;
		border-bottom: 1px solid #f0f0f0;
	}

	&__title {
		font-size: 14px;
		font-weight: 500;
		color: #4D4D4D;
	}

	&__list {
		max-height: 400px;
		overflow-y: auto;
		padding: 6px 0;
	}

	&__collapsible {
		display: grid;
		grid-template-rows: 1fr;
		transition: grid-template-rows 0.4s ease;

		&--collapsed {
			grid-template-rows: 0fr;
		}
	}

	&__collapsible-inner {
		overflow: hidden;
		min-height: 0;
	}

	&__toggle {
		transition: transform 0.3s ease;
		transform: rotate(180deg);

		&--collapsed {
			transform: rotate(0deg);
		}
	}
}

.upload-queue-item {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px 12px;
	min-height: 44px;
	box-sizing: border-box;

	&__icon {
		flex: 0 0 auto;
	}

	&__body {
		flex: 1 1 auto;
		min-width: 0;
	}

	&__row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
	}

	&__name {
		flex: 1 1 auto;
		min-width: 0;
		font-size: 12px;
		color: #4D4D4D;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__meta {
		flex: 0 0 auto;
		font-size: 11px;
		color: #4D4D4D;
		white-space: nowrap;
	}

	&--error &__meta {
		color: #e5484d;
	}

	&--success &__meta {
		color: #438451;
	}

	&__progress {
		margin-top: 6px;
		height: 4px;
		background: #eef0f3;
		border-radius: 3px;
		overflow: hidden;
	}

	&__progress-bar {
		height: 100%;
		background: #438451;
		border-radius: 3px;
		transition: width 0.15s ease;
	}
}
</style>
